import {Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {AppComponent} from "../../app.component";
import {Question} from "../../models/question.model";
import {AnswerExplanation} from "../../models/answer.model";
import {Router} from "@angular/router";
import {RouterLinks} from "../../enums/router-links";
import {NavigationEnd} from '@angular/router';
import {QstTypes} from "../../enums/qst-types";
import {RespTypes} from "../../enums/resp-types";
import {ContentForListButtons} from "../../models/module.model";
import {ModalTypes} from "../../enums/modal-types";
import {ModalTemplates} from "../../enums/modal-templates";
import {titles} from "../../const/texts-common-sections";
import {ScreenTypes} from "../../enums/screen-types";
import {AuthGuard} from "../../helpers/auth-guard.helper";
import {Subject, takeUntil} from "rxjs";

@Component({
  selector: 'app-question',
  templateUrl: './question.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./question.component.less']
})
export class QuestionComponent implements OnInit, OnDestroy {
  @ViewChild('buttonTryAgain') buttonTryAgain!: ElementRef;
  @ViewChild('buttonCheckAnswer') buttonCheckAnswer!: ElementRef;
  @ViewChild('buttonNext') buttonNext!: ElementRef;

  global_texts: any = null;
  question!: Question;
  answer: (number | null)[] = [];
  is_answer_explanation_visible = false;
  answer_explanation!: AnswerExplanation | null;
  answer_explanation_type: RespTypes = RespTypes.Default;
  answer_explanation_other_datas: any[] | null = null;
  small_texts_answer_explanation: string[] = [];
  annexes_answer_explanation: string[] | null = null;
  has_button_try_again: boolean = false;
  current_qst_index: number = 0;
  current_qst_class: string = '';
  current_screen_annexes: string[] | null = null;
  current_screen_small_texts: string[] | null = null;
  current_screen_asterisks: string[] | null = null;
  current_qst_additional_text: string = '';
  current_qst_additional_text_drop_zone: string = '';
  answer_is_correct: boolean = false;
  private unsubscribe$ = new Subject<void>();

  constructor(
    public app: AppComponent,
    private router: Router,
    private authGuard: AuthGuard
  ) {
    // Subscribe to router events
    // this.router.events.subscribe((event) => {
    this.router.events.pipe(takeUntil(this.unsubscribe$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        console.log("NavigationEnd event QST => ", event);
        this.initializeComponentQst();
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnInit(): void {
    this.initializeComponentQst();
  }

  initializeComponentQst(): void {
    // Verify if the logout button was clicked
    if (this.authGuard.isLogoutClicked) {
      return;
    }

    // Scroll to the top of the page
    window.scrollTo(0, 0);

    // Reset the data
    this.answer = [];
    this.is_answer_explanation_visible = false;
    this.answer_explanation = null;
    this.current_qst_additional_text = '';
    this.current_qst_additional_text_drop_zone = '';
    this.current_qst_class = '';
    this.current_screen_annexes = null;
    this.current_screen_small_texts = null;
    this.current_screen_asterisks = null;
    this.has_button_try_again = false;
    this.small_texts_answer_explanation = [];
    this.annexes_answer_explanation = null;
    this.answer_explanation_type = RespTypes.Default;
    this.answer_explanation_other_datas = null;

    // Blur the buttons
    if (this.buttonNext) this.buttonNext.nativeElement.blur();
    if (this.buttonTryAgain) this.buttonTryAgain.nativeElement.blur();
    if (this.buttonCheckAnswer) this.buttonCheckAnswer.nativeElement.blur();

    // Get the current question params and index
    this.current_qst_index = this.getTheCurrentQstIndex();
    this.current_qst_class = this.app.sharedHelper.getCurrentScreenParam('special_class');
    this.has_button_try_again = this.app.sharedHelper.getCurrentScreenParam('has_button_try_again') || false;

    // Small texts, annexes and asterisks of the current question
    let current_screen_small_texts = this.app.sharedHelper.getCurrentScreenParam('small_texts');
    if (current_screen_small_texts) this.current_screen_small_texts = Object.values(current_screen_small_texts);
    this.current_screen_annexes = this.app.sharedHelper.getCurrentScreenParam('annexes');
    this.current_screen_asterisks = this.app.sharedHelper.getCurrentScreenParam('asterisks');

    // Get the global texts
    this.app.sharedHelper.getGlobalTexts().subscribe((texts: any) => {
      this.global_texts = texts;

      // Get the current question additional text after the question label
      let current_qst_additional_text = this.app.sharedHelper.getCurrentScreenParam('annexe_after_question_label');
      if (current_qst_additional_text) this.current_qst_additional_text = this.global_texts[current_qst_additional_text];

      // Get the current question additional text in the drop zone
      let current_qst_additional_text_drop_zone = this.app.sharedHelper.getCurrentScreenParam('annexe_in_drop_zone');
      if (current_qst_additional_text_drop_zone) this.current_qst_additional_text_drop_zone = this.global_texts[current_qst_additional_text_drop_zone];

      // Get the current question ref from the url
      let pathname: string = window.location.pathname;
      const parts = pathname.split('/');
      let ref = parts[parts.length - 1];

      // Get the current question
      this.app.sharedHelper.getQuestion(ref).then((question: Question) => {
        this.question = question;
        console.log("initializeComponent this.question => ", this.question);
      });
    });
  }

  disableButtonValidate(): boolean {
    let boolean_to_return = false;
    if (!this.answer || this.answer.length === 0) {
      boolean_to_return = true;
    }
    if (this.question.type == QstTypes.DropzoneMultiple && this.answer.length > 0) {
      if (this.answer.length !== this.question.answers_options.length) {
        boolean_to_return = true;
      } else {
        let resp_null = this.answer.find((a: any) => a === null);
        if (resp_null !== undefined) {
          boolean_to_return = true;
        }
      }
    }
    return boolean_to_return;
  }

  getTheCurrentQstIndex(): number {
    let current_qst_index = 0;
    let ref = this.app.sharedHelper.getRefPageFromUrl();
    let current_screens = this.app.sharedHelper.getCurrentScreensTypeQst();

    if (current_screens && current_screens.length > 0) {
      current_qst_index = current_screens.findIndex((s: any) => s.ref === ref);
    }

    return current_qst_index + 1;
  }

  tryAgainSameQuestion() {
    let current_url = this.router.url;
    this.router.navigateByUrl('/', {skipLocationChange: true}).then(() => {
      this.router.navigate([current_url]);
    });
  }

  onAnswerSelected($event: (number | null)[]) {
    console.log("onAnswerSelected $event => ", $event);
    this.answer = $event;
  }

  setTheAnswerExplanation(is_correct: boolean, answer_object: any | null, answer_explanation_small_texts: any | null) {
    if (this.answer.length > 0) this.is_answer_explanation_visible = true;

    this.annexes_answer_explanation = (answer_object && answer_object.annexes) ? answer_object.annexes : null;
    this.answer_explanation_type = (answer_object) ? answer_object.type : this.answer_explanation_type;
    this.answer_explanation_other_datas = (answer_object) ? answer_object.other_datas : null;

    this.answer_explanation = is_correct ? answer_object.is_correct : answer_object.is_not_correct;
    if (answer_explanation_small_texts) this.small_texts_answer_explanation = Object.values(answer_explanation_small_texts);
    this.answer_explanation!.color_title = this.getTheColorTitle(is_correct);
    this.answer_is_correct = is_correct;
  }

  checkAnswer() {
    let is_correct: boolean = false;

    let answer_object = this.app.sharedHelper.getOtherDatasForQstByRef(this.question.ref);
    let answer_explanation_small_texts = answer_object?.small_texts || null;

    // Set answer and check if the answer is correct calling the server
    this.app.apiService.addAnswer(this.answer, this.question.ref).subscribe((response: any) => {
      console.log("checkAnswer response => ", response);
      if (response?.data) {
        is_correct = response.data.is_correct;
        this.setTheAnswerExplanation(is_correct, answer_object, answer_explanation_small_texts);
      }
    }, (error: any) => {
      console.log("checkAnswer error => ", error);
    });
  }

  getTheColorTitle(is_correct: boolean): string {
    return is_correct ? 'green' : 'red';
  }

  openModalRespExplanation(other_data: any | null): void {
    this.app.dialogService.confirmThis(
      other_data.title,
      '',
      ModalTypes.Custom,
      ModalTemplates.InfoOnlyText,
      () => {
      },
      () => {
      },
      other_data.content_popup
    );
  }

  protected readonly QstTypes = QstTypes;
  protected readonly RespTypes = RespTypes;
  protected readonly titleCasComptoir1 = titles.titleCasComptoir1;
}
